/**
 * @file axios 接口相关配置
 * @date 2021-06-26
 */
import { getOrigin, isProdOrTestnet } from '@/config/env';
import { i18n } from '@/locales/i18n';
import axios from 'axios';

/**
 * 接口配置
 *
 * @type {Object}
 */
export const config = {
    '@spot/': {
        url() {
            return `${getOrigin({ type: 'api' })}/spot/`;
        },
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    },
    '@spotJson/': {
        url() {
            return `${getOrigin({ type: 'api' })}/spot/`;
        },
        headers: { 'Content-Type': 'application/json' },
    },
    '@api2/': {
        url() {
            return `${getOrigin({ type: 'api' })}/`;
        },
    },
    '@data/': {
        url() {
            return `${getOrigin({ type: 'api' })}/data-eye/`;
        },
    },
    '@unified/': {
        url() {
            return `${getOrigin({ type: 'api' })}/unified/`;
        },
    },
    '@siteapi/': {
        url() {
            return `${getOrigin({ type: 'api' })}/siteapi/`;
        },
    },
    '@deadpoolapi2/': {
        url() {
            // if (isProdOrTestnet()) {
            return `${getOrigin({ type: 'api' })}/`;
            // }
            // return 'http://api2.cht-test-1.bybit.com/';
        },
    },
    '@notice/': {
        url() {
            return `${getOrigin({ type: 'api' })}/notification-site/`;
        },
    },
    '@bot/': {
        url() {
            return `${getOrigin({ type: 'api' })}/s1/bot/grid/v1/`;
        },
    },
    '@s1/': {
        url() {
            return `${getOrigin({ type: 'api' })}/s1/`;
        },
    },
};

/**
     * 现货多域名需求 - 替换 s1.bycsi.com 和 t1.bycsi.com 域名路径为相对路径
     * 现货的币种图标目前都是用的是 s1.bycsi.com 域名下的资源，为了方式 bycsi.com 域名被墙，改为使用当前入口域名下的 /bycsi-root 相对路径来访问链接。
     * Nginx 下做了相对应的映射 https://主域/bycsi-root -> s1.bycsi.com
     *
     * @param {string} res 接口数据
     * @return {string} res 返回路径替换后的接口数据
     */
export const bycsiDomainTransform = (res) => {
    const bycsiDomainReg = /https?:\/\/(?:s|t)1\.bycsi\.com/g;
    // 数据类型为 string，其他类型不做处理。因为其他数据类型并不包含链接内容
    // 只对 testnet 和 prd 环境做处理，因为测试环境运维当前没有配置相关的 Nginx 映射
    // 检测链接中是否带有 s1.bycsi.com 或者 t1.bycsi.com 的链接，如果带有则替换成相对路径
    if (typeof res === 'string' && ['prd'].includes(window.sfe.env) && bycsiDomainReg.test(res)) {
        return res.replace(bycsiDomainReg, '/bycsi-root');
    }
    return res;
};

/**
     * axios 实例化参数
     *
     * @type {Object}
     */
export const options = {
    timeout: 30000,
    headers: {
        platform: 'pc',
        post: {
            'Content-Type': 'application/json;charset=UTF-8',
        },
    },
    withCredentials: true, // 跨域请求携带cookie
    emulateJSON: true,
    transformResponse: [
        bycsiDomainTransform,
        ...axios.defaults.transformResponse,
    ],
};

/**
     * 错误处理方法
     *
     * @param {Object} err 错误对象
     * @return {Object}
     */
export const error = (err) => {
    let code = 500;
    let errMsg = i18n?.global?.t('network_error') || 'Request failed. Please try again later.';

    try {
        if (err.response) {
            const res = err.response;
            const data = res.data || {};

            code = data.ext_code || res?.request?.status || res?.status || code;
            errMsg = data.ret_msg || data.error || errMsg;
        }
    } catch (e) {
        //
    }

    return {
        success: false,
        code,
        message: errMsg,
        data: null,
    };
};

// 兼容接口返回的小驼峰 返回值，以及其msg 不支持中文，会用urlEncode编码，这里需要解码
function adaptResponse(data) {
    if (!data) {
        return;
    }
    if (data.ret_code === undefined) {
        data.ret_code = data.retCode;
    }
    if (data.ret_msg === undefined) {
        try {
            data.ret_msg = decodeURIComponent(data.retMsg);
        } catch {
            data.ret_msg = data.retMsg;
        }
    }
}

/**
 * 格式化响应数据
 *
 * @param {Object} res axios Response Schema: https://github.com/axios/axios#response-schema
 * @return {Object} 返回响应数据结果对象
 */
export const transformResponse = (res) => {
    let data = res.request && res.config ? res.data : res;
    adaptResponse(data);
    // 响应拦截器
    if (res.config && typeof res.config.interceptRes === 'function') {
        data = res.config.interceptRes(data, res);
    }

    // 如果不是正常 JSON 数据则认为是错误
    if (typeof data !== 'object') {
        return error('数据响应格式不正确。');
    }
    // 封装后端 Response Schema
    // 具体需要根据后端而定
    data.success = data.ret_code === 0;
    data.code = data.ret_code;
    data.message = !data.ret_msg && data.ret_code !== 0 ? 'Access error, please try again later' : data.ret_msg;
    data.result = Object.prototype.hasOwnProperty.call(data, 'result') ? data.result : null;
    return data;
};
